import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

import Loading from '../../Loading';
import FileUploadField from '../../FileUploadField';
import WarningPanel from '../../WarningPanel';

import { useTimeModal } from '../../TimeModal/TimeModalContext';

import { parseAccountingFile } from '../../../Utils/ParseAccountingFile';
import { formatDate } from '../../../Utils/GlobalUtils';

const SROAccountingDataMerge = () => {
    const { openTimeModal } = useTimeModal();
    const navigate = useNavigate();
    const [contentForm, setContentForm] = React.useState('selectFile');
    const [isBottomBtn, setIsBottomBtn] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [warning, setWarning] = React.useState('');
    const [status, setStatus] = React.useState('');

    const [previewData, setPreviewData] = React.useState(null);
    const [dateStart, setDateStart] = React.useState('');
    const [dateEnd, setDateEnd] = React.useState('');

    const [files, setFiles] = React.useState({
        accounting: null,
    });
    const handleFileChange = (field) => (e) => {
        const file = e.target.files[0];
        setFiles((prev) => ({
            ...prev,
            [field]: file || null,
        }));
    };
    const handleFileReset = (field) => () => {
        setFiles((prev) => ({
            ...prev,
            [field]: null,
        }));
    };

    React.useEffect(() => {
        if (dateStart && dateEnd && files.accounting) setIsBottomBtn(true);
    }, [dateStart, dateEnd, files.accounting]);

    const [allAMBalances, setAllAMBalances] = React.useState([]);

    React.useEffect(() => {
        const token = localStorage.getItem('token');

        axios.get('/api/get-all-am-balances', {
            headers: {
                Authorization: `Bearer ${token}`
            }
        })
        .then((response) => {
            setWarning('');
            setAllAMBalances(response.data);
        })
        .catch((error) => {
            console.error(error);
            setWarning(`${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`);
        })
        .finally(() => {
            setLoading(false);
        });
    }, []);

    const amBalancesMap = React.useMemo(() => {
        const map = new Map();
        allAMBalances.forEach((am) => map.set(am.fio, am));
        return map;
    }, [allAMBalances]);

    const enrichedPreviewData = React.useMemo(() => {
        if (!previewData) return previewData;
        return previewData.map((item) => {
            const matchedAM = amBalancesMap.get(item.fio);
            return matchedAM
                ? { ...item, user_id: matchedAM.id }
                : item;
        });
    }, [previewData, amBalancesMap]);

    function removeOperation(fio, opIndex, type_balance) {
        setPreviewData(prev => prev.map(item => {
            if (item.fio !== fio) return item;

            if (type_balance=='balance') {
                const updatedOperations = item.operations.filter((_, i) => i !== opIndex);

                const opsSum = updatedOperations.reduce(
                    (acc, op) => acc - (op.debit || 0) + (op.credit || 0),
                    0
                );

                return {
                    ...item,
                    operations: updatedOperations,
                    turnover: opsSum,
                };
            } else {
                const updatedOperations = item.special_operations.filter((_, i) => i !== opIndex);

                const opsSum = updatedOperations.reduce(
                    (acc, op) => acc - (op.debit || 0) + (op.credit || 0),
                    0
                );

                return {
                    ...item,
                    special_operations: updatedOperations,
                    special_turnover: opsSum,
                };
            }
            
        }));
    }

    const handleChangeDateEnd = (e) => {
        if (dateStart >= e.target.value) {
            setIsBottomBtn(false);
            return openTimeModal('Дата начала периода должна быть меньше даты конца периода', 'error');
        } else {
            setDateEnd(e.target.value);
        }
    }

    const handleSubmit = async () => {
        if (!files.accounting) return;

        setLoading(true);
        setWarning('');

        try {
            const arrayBuffer = await files.accounting.arrayBuffer();
            const parsed = parseAccountingFile(arrayBuffer, formatDate(dateStart), formatDate(dateEnd));

            if (parsed.length === 0) {
                setWarning('Загруженный файл не содержит данных для обработки. Пожалуйста, проверьте корректность файла и повторите попытку');
            } else {
                setPreviewData(parsed);
            }
        } catch (err) {
            console.error(err);
            setWarning('Не удалось прочитать файл. Проверьте, что это корректный .xlsx');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        const token = localStorage.getItem('token');

        setLoading(true);


        const formData = new FormData();

        formData.append('previewData', JSON.stringify(enrichedPreviewData));
        formData.append('date_start', dateStart);
        formData.append('date_end', dateEnd);

        if (files.accounting) {
            formData.append('file_accounting_sro', files.accounting);
        }

        axios.post('/api/sro-accounting-data-merge', formData, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
            }
        })
        .then((response) => {
            setWarning('');
            openTimeModal('Данные успешно объединены', 'success');
            setStatus(response.data.message);
            navigate('/membership-fees?section=am-balances');
        })
        .catch((error) => {
            console.error(error);
            setWarning(`${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`);
        })
        .finally(() => {
            setLoading(false);
        });
    };

    function applyCompensation(item, compensation, description, type_balance) {
        if (!compensation) return item;

        const newOp = {
            description: description,
            debit: compensation < 0 ? -compensation : 0,
            credit: compensation > 0 ? compensation : 0,
        };

        if (type_balance=='special_balance') {
            const updatedOperations = [...item.special_operations, newOp];
            return {
                ...item,
                special_operations: updatedOperations,
                special_turnover: item.special_turnover + compensation,
            };
        } else {
            const updatedOperations = [...item.operations, newOp];
            return {
                ...item,
                operations: updatedOperations,
                turnover: item.turnover + compensation,
            };
        }
    }

    function addCompensatingOperation(fio, amount, description, type_balance) {
        setPreviewData(prev => prev.map(item => (item.fio !== fio ? item : applyCompensation(item, amount, description, type_balance))));
    }

    function addCompensatingOperationsForAll(type_balance) {
        setPreviewData(prev => prev.map(item => {
            const matchedAM = amBalancesMap.get(item.fio);
            if (!matchedAM) return item; // нет совпадения — не трогаем

            const isSpecial = type_balance === 'special_balance';

            const currentTurnover = isSpecial ? item.special_turnover : item.turnover;
            const targetEndBalance = isSpecial ? item.special_end_balance : item.end_balance;
            const amBalance = isSpecial ? matchedAM.special_balance : matchedAM.balance;

            const compensation = round2(
                Number(targetEndBalance) - (Number(amBalance) + Number(currentTurnover))
            );

            if (!compensation) return item; // уже сведено — ничего не добавляем

            return applyCompensation(
                item,
                compensation,
                'Техническая операция для приведения баланса',
                type_balance
            );
        }));
    }
    function round2(n) {
        return Math.round((n + Number.EPSILON) * 100) / 100;
    }

    return (
        <div className='pageLimiter'>
            <div className='stepsPanel'>
                {contentForm=='selectFile' &&
                <div className='step'>
                    <h3>Загрузка файла</h3>
                </div>
                }
                {contentForm=='previewData' &&
                <>
                    <div className='step'>
                        <h3 onClick={() => {setContentForm('selectFile')}}>Загрузка файла</h3>
                    </div>
                    <div className='step'>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" id="angle-right-b"><path fill="currentColor" d="M15.54,11.29,9.88,5.64a1,1,0,0,0-1.42,0,1,1,0,0,0,0,1.41l4.95,5L8.46,17a1,1,0,0,0,0,1.41,1,1,0,0,0,.71.3,1,1,0,0,0,.71-.3l5.66-5.65A1,1,0,0,0,15.54,11.29Z"></path></svg>
                    </div>
                    <div className='step'>
                        <h3 onClick={() => {setContentForm('previewData')}}>Предварительный просмотр данных</h3>
                    </div>
                </>
                }
            </div>
            <WarningPanel text='Предварительная версия. Возможна некорректная работа' type='warning' />
            {loading ? <Loading /> :
            <>
                {contentForm=='selectFile' &&
                <>
                    <div className='labelInputWrapper'>
                        <a className='label'>Начало периода ведомости*</a>
                        <div className='labelInputRight'>
                            <div className='inputWrapper'>
                                <input type='date' 
                                value={dateStart?.split('T')[0]}
                                onChange={(e) => setDateStart(e.target.value)} 
                                placeholder='' 
                                minLength='' 
                                maxLength='' 
                                required />
                            </div>
                        </div>
                    </div>
                    <div className='labelInputWrapper'>
                        <a className='label'>Конец периода*</a>
                        <div className='labelInputRight'>
                            <div className='inputWrapper'>
                                <input type='date' 
                                value={dateEnd?.split('T')[0]}
                                onChange={handleChangeDateEnd} 
                                placeholder='' 
                                minLength='' 
                                maxLength='' 
                                required />
                            </div>
                        </div>
                    </div>
                    <div className='labelInputWrapper'>
                        <a className='label'>Файл*</a>
                        <div className='labelInputRight'>
                            <FileUploadField
                                label='Загрузить файл оборотно-сальдовой ведомости по счету 76.09'
                                file={files.accounting}
                                onChange={handleFileChange('accounting')}
                                onReset={handleFileReset('accounting')}
                                allowedFileTypes={['xlsx']}
                                isAlreadyOpened={true}
                                isCloseButton={false}
                            />
                        </div>
                    </div>
                    <div className='panelFormConfirmation'>
                        <div></div>
                        {isBottomBtn && <button type='button' className='button' onClick={(e) => { handleSubmit(); setContentForm('previewData') }}>Далее</button>}
                    </div>
                </>
                }
                {contentForm=='previewData' &&
                <>
                    <>
                        {loading ? <Loading />
                        : warning ? <WarningPanel text={warning} />
                        :
                            <>
                                <div className='horizontalContainer'>
                                    <p className='description'>
                                        Сравнять баланс всем управляющим:
                                    </p>
                                    <span className='horizontalContainer'>
                                        <button type='button' className='miniButton' style={{width: 'fit-content'}} onClick={() => addCompensatingOperationsForAll('balance')}>
                                            по членским взносам
                                        </button>
                                        <button type='button' className='miniButton' style={{width: 'fit-content'}} onClick={() => addCompensatingOperationsForAll('special_balance')}>
                                            по целевым взносам
                                        </button>
                                    </span>
                                </div>
                                {enrichedPreviewData.map((item) => {
                                    const matchedAM = amBalancesMap.get(item.fio);

                                    return (
                                        <div className='panel' key={item.fio}>
                                            <div className='panelHeader'>
                                                <h3>{item.fio}</h3>
                                            </div>
                                            {matchedAM ? (
                                                <>
                                                    <div className='panel' style={{fontSize: '14px'}}>
                                                        <p className='description'>Членские взносы:</p>
                                                        <p>Текущий баланс: {matchedAM.balance}</p>
                                                        {/* <p>Текущий user_id: {item.user_id}</p>
                                                        <p>item.turnover: {item.turnover}</p>
                                                        <p>item.endbalance: {item.end_balance}</p> */}
                                                        <p>Ожидаемый баланс после операций: {Number(matchedAM.balance)+Number(item.turnover)}</p>
                                                        <p>Необходимый баланс исходя из сальдо на конец периода: {item.end_balance}</p>
                                                        <div className='horizontalContainer'>
                                                            <p>Необходимая сумма компенсирующей операции для свода баланса: {Number(item.end_balance)-(Number(matchedAM.balance)+Number(item.turnover))}</p>
                                                            {(Number(item.end_balance)-(Number(matchedAM.balance)+Number(item.turnover))) !== 0 &&
                                                                <a className='aWithLink' onClick={() => addCompensatingOperation(item.fio, (Number(item.end_balance)-(Number(matchedAM.balance)+Number(item.turnover))), 'Техническая операция для приведения баланса членских взносов', 'balance')}>
                                                                    Добавить компенсирующую операцию
                                                                </a>
                                                            }
                                                        </div>
                                                    </div>
                                                    {item.operations.map((op, opIndex) => (
                                                        <div className='panel' key={opIndex} style={{flexDirection: 'row', justifyContent: 'space-between', fontSize: '14px'}}>
                                                            <p style={{width: '40%'}}>{op.description}</p>
                                                            <div style={{width: '20%'}}>
                                                                <p className={op.credit ? 'colorCell green' : op.debit ? 'colorCell red' : ''} style={{textAlign: 'center'}}>
                                                                    {op.credit ? 'Пополнение' : op.debit ? 'Списание' : 'Ошибка'}
                                                                </p>
                                                            </div>
                                                            <p style={{width: '20%'}}>
                                                                {op.credit ? '+ ' + op.credit : op.debit ? '- ' + op.debit : 'Ошибка'} ₽
                                                            </p>
                                                            <a style={{width: '20%'}} className='aWithLink' onClick={() => removeOperation(item.fio, opIndex, 'balance')}>
                                                                Удалить
                                                            </a>
                                                        </div>
                                                    ))}
                                                    <div className='panel' style={{fontSize: '14px'}}>
                                                        <p className='description'>Целевые взносы:</p>
                                                        <p>Текущий баланс: {matchedAM.special_balance}</p>
                                                        {/* <p>Текущий user_id: {item.user_id}</p>
                                                        <p>item.turnover: {item.special_turnover}</p>
                                                        <p>item.endbalance: {item.special_end_balance}</p> */}
                                                        <p>Ожидаемый баланс после операций: {Number(matchedAM.special_balance)+Number(item.special_turnover)}</p>
                                                        <p>Необходимый баланс исходя из сальдо на конец периода: {item.special_end_balance}</p>
                                                        <div className='horizontalContainer'>
                                                            <p>Необходимая сумма компенсирующей операции для свода баланса: {Number(item.special_end_balance)-(Number(matchedAM.special_balance)+Number(item.special_turnover))}</p>
                                                            {(Number(item.special_end_balance)-(Number(matchedAM.special_balance)+Number(item.special_turnover))) !== 0 &&
                                                                <a className='aWithLink' onClick={() => addCompensatingOperation(item.fio, (Number(item.special_end_balance)-(Number(matchedAM.special_balance)+Number(item.special_turnover))), 'Техническая операция для приведения баланса целевых взносов', 'special_balance')}>
                                                                    Добавить компенсирующую операцию
                                                                </a>
                                                            }
                                                        </div>
                                                    </div>
                                                    {item.special_operations.map((op, opIndex) => (
                                                        <div className='panel' key={opIndex} style={{flexDirection: 'row', justifyContent: 'space-between', fontSize: '14px'}}>
                                                            <p style={{width: '40%'}}>{op.description}</p>
                                                            <div style={{width: '20%'}}>
                                                                <p className={op.credit ? 'colorCell green' : op.debit ? 'colorCell red' : ''} style={{textAlign: 'center'}}>
                                                                    {op.credit ? 'Пополнение' : op.debit ? 'Списание' : 'Ошибка'}
                                                                </p>
                                                            </div>
                                                            <p style={{width: '20%'}}>
                                                                {op.credit ? '+ ' + op.credit : op.debit ? '- ' + op.debit : 'Ошибка'} ₽
                                                            </p>
                                                            <a style={{width: '20%'}} className='aWithLink' onClick={() => removeOperation(item.fio, opIndex, 'special_balance')}>
                                                                Удалить
                                                            </a>
                                                        </div>
                                                    ))}
                                                </>
                                            ) : (
                                                <p className='description'>Пользователь не найден в системе</p>
                                            )}
                                        </div>
                                    );
                                })}
                            </>
                        }
                    </>
                    <div className='panelFormConfirmation'>
                        <p>{status}</p>
                        <button type='button' className='button' onClick={(e) => { handleSave() }}>Подтвердить слияние данных</button>
                    </div>
                </>
                }
            </>}
        </div>
    )
}

export default SROAccountingDataMerge;